import {
  appendUniqueVideoSegment,
  getDeoptimizedScan,
  getOptimizedScan,
  getScanDataInMemoryDeltaFromScanRoots,
  getScanPartsByScanPartId,
  getScanRootFromScanData,
  getUniqueScanPhotos,
  SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH,
  ScanAudio,
  ScanPart,
  ScanPartOptimized,
  ScanRoot,
  ScanRootOptimized,
  ScanVideo,
  transformScanRootOptimized,
} from '@kaiizen/kai-scan-template-system';
import type { ApiCreateScanDataSuggestion } from './ApiSchemas';
import { KaiProApiClient } from './KaiProApiClient';

/**
 * Fetches the current version of a scan and assembles it into a ScanRootOptimized —
 * the same in-memory tree shape pure functional scan edits operate on.
 */
export const getScanRootOptimizedForScan = async (
  client: KaiProApiClient,
  scanId: string,
): Promise<ScanRootOptimized> => {
  const [scanTemplateRoot, { data: scanData }] = await Promise.all([
    client.getScanTemplateRootForScan(scanId),
    client.getScanData(scanId),
  ]);
  return getOptimizedScan(getScanRootFromScanData(scanData), scanTemplateRoot);
};

/**
 * Diffs a before/after ScanRootOptimized pair down to the exact ScanDataInMemory records
 * that changed, then writes just that delta to the backend. Named to pair with the existing
 * getScanDataInMemoryDeltaFromScanRoots helper — same before/after contract, but it also saves.
 */
export const saveScanDataDeltaFromScanRoots = async (
  client: KaiProApiClient,
  scanId: string,
  scanRootOptimizedBefore: ScanRootOptimized,
  scanRootOptimizedAfter: ScanRootOptimized,
  timeoutSeconds?: number,
): Promise<{ recordsWritten: number }> => {
  const delta = getScanDataInMemoryDeltaFromScanRoots(
    scanId,
    scanRootOptimizedBefore,
    scanRootOptimizedAfter,
  );
  console.log(`Saving scan data delta for scan ${scanId} with ${delta.length} records...`);
  if (delta.length === 0) {
    return { recordsWritten: 0 };
  }
  return client.saveScanDataDelta(scanId, delta, timeoutSeconds);
};

/**
 * Helper for saveNewScanPartFields.
 * Combines the photos of the "before" and "after" scan parts into one list, without duplicates.
 * The "before" photos come first and are kept as they are, and any photos from the "after" scan
 * with a `photoId` that isn't already there are added after them.
 * If no photo was added, the "before" scan part itself is returned, so that it is left untouched.
 * @param scanPartFromBefore The scan part from the "before" scan.
 * @param scanPartFromAfter The scan part from the "after" scan.
 * @param tsNow The timestamp (unix ms) to set as `agentUpdatedAt` if photos are added.
 * @returns The scan part with new photos added if applicable.
 */
const getScanPartWithNewPhotos = (
  scanPartFromBefore: ScanPartOptimized,
  scanPartFromAfter: ScanPartOptimized,
  tsNow: number,
): ScanPartOptimized => {
  const photosBefore = scanPartFromBefore.photos ?? [];
  const photoIdsBefore = new Set(photosBefore.map(photo => photo.photoId));

  // Only the "after" photos that aren't already there are new. The "before" photos are left
  // exactly as they are, even when the "after" scan has the same photoId with other information.
  const photosNew = getUniqueScanPhotos(scanPartFromAfter.photos ?? [])
    .filter(photo => !photoIdsBefore.has(photo.photoId));
  if (photosNew.length === 0) {
    // No new photos to add.
    return scanPartFromBefore;
  }

  const scanPartWithNewPhotos = {
    ...scanPartFromBefore,
    photos: [...photosBefore, ...photosNew],
    agentUpdatedAt: tsNow,
  };
  return scanPartWithNewPhotos;
};

/**
 * Helper for saveNewScanPartFields.
 * Takes any values in the "after" scan part that are not yet set in the "before" scan part,
 * and adds them. Doesn't overwrite or modify any values that are already set in the "before"
 * scan part.
 * @param scanPartFromBefore The scan part from the "before" scan.
 * @param scanPartFromAfter The scan part from the "after" scan.
 * @param tsNow The timestamp (unix ms) to set as `agentUpdatedAt` if values are added.
 * @returns The scan part with new values added if applicable.
 */
const getScanPartWithNewValues = (
  scanPartFromBefore: ScanPartOptimized,
  scanPartFromAfter: ScanPartOptimized,
  tsNow: number,
): ScanPartOptimized => {
  if (!scanPartFromAfter.values) {
    // No values to add.
    return scanPartFromBefore;
  }

  // Start from the "before" values, and fill in the ones that are missing.
  const valuesWithNewValues = { ...scanPartFromBefore.values };
  let anyValueAdded = false;
  for (const [key, valueAfter] of Object.entries(scanPartFromAfter.values)) {
    if (valueAfter.setValue == null) {
      // The "after" scan didn't set this value, so there is nothing to add.
      continue;
    }
    if (valuesWithNewValues[key]?.setValue != null) {
      // Already set. The existing value is probably more correct than
      // the new value from the "after" scan, so we won't overwrite it.
      continue;
    }
    // Only copy the set value. The calculated value is recomputed from it.
    valuesWithNewValues[key] = { ...valuesWithNewValues[key], setValue: valueAfter.setValue };
    anyValueAdded = true;
  }

  if (!anyValueAdded) {
    // Return the very same scan part, so that it is left untouched.
    return scanPartFromBefore;
  }
  const scanPartWithNewValues = {
    ...scanPartFromBefore,
    values: valuesWithNewValues,
    agentUpdatedAt: tsNow,
  };
  return scanPartWithNewValues;
};

/**
 * Helper for saveNewScanPartFields.
 * If the scan part from the "before" scan already has audio, it is kept as is.
 * Otherwise, audio from the "after" scan is added if available.
 * @param scanPartFromBefore The scan part from the "before" scan.
 * @param scanPartFromAfter The scan part from the "after" scan.
 * @param tsNow The timestamp (unix ms) to set as `agentUpdatedAt` if audio is added.
 * @returns The scan part with new audio added if applicable.
 */
const getScanPartWithNewAudio = (
  scanPartFromBefore: ScanPartOptimized,
  scanPartFromAfter: ScanPartOptimized,
  tsNow: number,
): ScanPartOptimized => {
  const audioFromBefore: ScanAudio[] | undefined = (scanPartFromBefore as any).audio;
  if (audioFromBefore && audioFromBefore.length > 0) {
    // Already has audio. The existing audio is probably more correct than
    // the new audio from the "after" scan, so we won't overwrite it.
    return scanPartFromBefore;
  }

  const audioFromAfter: ScanAudio[] | undefined = (scanPartFromAfter as any).audio;
  if (!audioFromAfter || audioFromAfter.length === 0) {
    // No new audio to add.
    return scanPartFromBefore;
  }

  const scanPartWithNewAudio = {
    ...scanPartFromBefore,
    audio: audioFromAfter,
    agentUpdatedAt: tsNow,
  };
  return scanPartWithNewAudio;
};

/**
 * Helper for saveNewScanPartFields.
 * Combines the video segments of the "before" and "after" scan parts into one list, without duplicates.
 * The "before" video segments come first and are kept as they are, and any video segments from the "after"
 * scan that aren't already there are added after them. Two video segments are the same if they come from
 * the same video and cover the same time range.
 * A video segment that has no `videoOffset` gets the latest `videoOffset` known for the same video, from
 * either scan part. "Latest" means the highest `addedAt`, and on a tie the first one found, with the
 * "before" scan part's segments searched first. A "before" video segment that gets a `videoOffset` keeps
 * everything else it had. A segment that becomes the same as one already in the list is dropped.
 * If the combined list is the same as the "before" list, nothing changed, and the "before" scan part itself
 * is returned, so that it is left untouched.
 * @param scanPartFromBefore The scan part from the "before" scan.
 * @param scanPartFromAfter The scan part from the "after" scan.
 * @param tsNow The timestamp (unix ms) to set as `agentUpdatedAt` if the video segments change.
 * @returns The scan part with the combined video segments, or the "before" scan part if nothing changed.
 */
const getScanPartWithNewVideos = (
  scanPartFromBefore: ScanPartOptimized,
  scanPartFromAfter: ScanPartOptimized,
  tsNow: number,
): ScanPartOptimized => {
  // Deep-copy to prevent mutating the original video arrays from the scan parts.
  const videosFromBefore: ScanVideo[] = JSON.parse(JSON.stringify((scanPartFromBefore as any).videos ?? []));
  const videosFromAfter: ScanVideo[] = JSON.parse(JSON.stringify((scanPartFromAfter as any).videos ?? []));

  // Build a map of the latest video offsets for each video ID,
  // for those video objects that have a video offset defined.
  const videosAll: ScanVideo[] = [...videosFromBefore, ...videosFromAfter];
  const mapVideoIdToLatestOffset: Record<string, {
    startSeconds: number;
    endSeconds: number;
    addedAt: number;
  }> = {};

  for (const video of videosAll) {
    if (!video.videoOffset) {
      continue;
    }
    const key = video.videoId;
    if (!mapVideoIdToLatestOffset[key] || mapVideoIdToLatestOffset[key].addedAt < (video.addedAt ?? 0)) {
      mapVideoIdToLatestOffset[key] = {
        startSeconds: video.videoOffset.startSeconds,
        endSeconds: video.videoOffset.endSeconds,
        addedAt: video.addedAt ?? 0,
      };
    }
  }

  let didAnythingChange: boolean = false;
  let videosMerged: ScanVideo[] = [];

  // Add the videos from the "before" scan part first,
  // applying the latest offsets if missing.
  // We expect every video from the "before" scan part to be added to the merged array.
  for (const video of videosFromBefore) {
    if (!video.videoOffset) {
      const latestOffset = mapVideoIdToLatestOffset[video.videoId];
      if (latestOffset) {
        video.videoOffset = {
          startSeconds: latestOffset.startSeconds,
          endSeconds: latestOffset.endSeconds,
        };
        didAnythingChange = true;
      }
    }

    const arrayLengthBefore = videosMerged.length;
    videosMerged = appendUniqueVideoSegment(videosMerged, video);

    const wasAdded = videosMerged.length > arrayLengthBefore;
    if (!wasAdded) {
      // We're in the videosFromBefore loop.
      // We *expect* this video to be added to the merged array.
      // If it's *not* added, that means that the emitted array
      // will be different from the original.
      didAnythingChange = true;
    }
  }

  // Possibly add videos from the "after" scan part, applying the latest offsets if missing.
  // Only add videos that are not duplicates of ones already in the merged array.
  // If *any* video is added, the merged list differs from the original, so it counts as a change.
  // The logic is *mostly* the same as the videosFromBefore loop, but with two inverted cases
  // that are worth a separate loop:
  // - In the videosFromBefore loop, filling in a missing offset counts as a change. Here, it doesn't,
  //   because the video may turn out to be a duplicate.
  // - In the videosFromBefore loop, a video that is not added counts as a change (a duplicate was dropped).
  //   Here, a video that is added counts as a change.
  for (const video of videosFromAfter) {
    if (!video.videoOffset) {
      const latestOffset = mapVideoIdToLatestOffset[video.videoId];
      if (latestOffset) {
        video.videoOffset = {
          startSeconds: latestOffset.startSeconds,
          endSeconds: latestOffset.endSeconds,
        };
        // This doesn't count as a change yet because we aren't sure
        // that the video will actually be added.
      }
    }

    const arrayLengthBefore = videosMerged.length;
    videosMerged = appendUniqueVideoSegment(videosMerged, video);

    const wasAdded = videosMerged.length > arrayLengthBefore;
    if (wasAdded) {
      // We're in the videosFromAfter loop.
      // If it's added, that means that the emitted array
      // will be different from the original.
      didAnythingChange = true;
    }
  }

  if (!didAnythingChange) {
    return scanPartFromBefore;
  }

  const scanPartRetval = {
    ...scanPartFromBefore,
    videos: videosMerged,
    agentUpdatedAt: tsNow,
  };
  return scanPartRetval;
};

/**
 * Adds new photos, set values, audio and video segments to scan parts, without overwriting anything
 * that is already there.
 * Photos are added next to the existing ones, without duplicates. Audio is added only to scan parts that
 * don't have any yet. Values are added one at a time, only for the values that haven't been set yet. Video
 * segments are added next to the existing ones, without duplicates, and an existing video segment that has
 * no time range gets one from the "after" scan. This way, if a human has already attached photos, audio or
 * video segments, or set values, those are preserved, but whatever is still missing can be filled in.
 * Everything is saved in a single write, so a scan part that gets several kinds of new fields is
 * written once, with all of them.
 * @param client The KaiProApiClient instance to use for API calls.
 * @param scanId The ID of the scan to update.
 * @param scanRootOptimizedBefore The scan root before any new fields were added.
 * @param scanRootOptimizedAfter The scan root containing the new fields to add.
 * @param timeoutSeconds Optional timeout, in seconds, for the call that saves the changes.
 * @returns An object indicating the number of records written to the backend.
 */
export const saveNewScanPartFields = async (
  client: KaiProApiClient,
  scanId: string,
  scanRootOptimizedBefore: ScanRootOptimized,
  scanRootOptimizedAfter: ScanRootOptimized,
  timeoutSeconds?: number,
): Promise<{ recordsWritten: number }> => {
  // Build a map that lets us quickly look up scan parts by their ID from the "after" scan.
  const scanPartsAfterById: Record<string, ScanPartOptimized> = getScanPartsByScanPartId(scanRootOptimizedAfter);

  const tsNow = Date.now();

  // Build a version of the "before" scan root that is exactly identical to the old one,
  // except that any new photos, values, audio and video segments from the "after" scan are added.
  const scanRootBeforeWithNewFields = transformScanRootOptimized(scanRootOptimizedBefore, scanPart => {
    const scanPartFromAfter = scanPartsAfterById[scanPart.id];
    if (!scanPartFromAfter) {
      // No corresponding scan part in the "after" scan.
      return scanPart;
    }

    scanPart = getScanPartWithNewPhotos(scanPart, scanPartFromAfter, tsNow);
    scanPart = getScanPartWithNewValues(scanPart, scanPartFromAfter, tsNow);
    scanPart = getScanPartWithNewAudio(scanPart, scanPartFromAfter, tsNow);
    scanPart = getScanPartWithNewVideos(scanPart, scanPartFromAfter, tsNow);
    return scanPart;
  });

  // Save only the difference between the "before" scan and the one with the new fields.
  // Nothing is written if nothing was added.
  const retval: { recordsWritten: number } = await saveScanDataDeltaFromScanRoots(
    client,
    scanId,
    scanRootOptimizedBefore,
    scanRootBeforeWithNewFields,
    timeoutSeconds,
  );
  return retval;
};

/**
 * Builds the suggestions for the scan parts that exist in the "after" scan but not in the "before" scan.
 * Each new scan part becomes one "add" suggestion under its parent, containing the scan part and everything
 * below it. For example, a new option becomes one suggestion under its task, with its line items inside, and
 * a new line item under an existing option becomes one suggestion under that option. Each one gets added to
 * its parent with an "add" operation; the "replace" and "remove" operations aren't used here.
 * This only builds the suggestion objects. It doesn't send them to the API; the caller will need to do that.
 * @param scanId The ID of the scan that the suggestions are for.
 * @param scanVideoId The ID of the scan video that the suggestions come from.
 * @param scanRootOptimizedBefore The scan root before any new scan parts were added.
 * @param scanRootOptimizedAfter The scan root containing the new scan parts.
 * @returns The suggestions to create, ready to send to the API.
 */
export const buildNewScanDataSuggestions = (
  scanId: string,
  scanVideoId: string,
  scanRootOptimizedBefore: ScanRootOptimized,
  scanRootOptimizedAfter: ScanRootOptimized,
): ApiCreateScanDataSuggestion[] => {
  // Build a map that lets us quickly look up scan parts by their ID from the "before" scan.
  const scanPartsBeforeById: Record<string, ScanPartOptimized> = getScanPartsByScanPartId(scanRootOptimizedBefore);

  // Suggestions hold plain scan parts, without the optimized fields.
  const scanRootAfter: ScanRoot = getDeoptimizedScan(scanRootOptimizedAfter);

  // Build the return value: the list of suggestions for new scan parts.
  // It starts empty and gets populated as we go.
  const suggestions: ApiCreateScanDataSuggestion[] = [];

  /**
   * Nested helper function to recursively add suggestions for new scan parts.
   * At every scan part, it checks if the part is new compared to the "before" scan.
   * If it is new, it adds a suggestion for it and stops recursion for that branch ("pops out").
   * If it is not new, it recurses into its children to look for new scan parts.
   * @param scanPart The scan part from the "after" scan that we'll check for prior presence in the "before" scan.
   * @param scanPartParentId The ID of the parent scan part from the "after" scan, used to set the parentScanPartId
   * in the suggestion. This is ignored for the root scan part, because the root is never considered new and therefore
   * is never included in the collection of suggestions.
   * @param depth The current depth in the scan part hierarchy, used to determine which children field to look at.
   * @returns void, but updates the suggestions array with new suggestions for any new scan parts found.
   */
  const recursiveAddSuggestions = (scanPart: ScanPart, scanPartParentId: string, depth: number) => {
    // Check if the current scan part is new compared to the "before" scan.
    const isNew = !(scanPart.id in scanPartsBeforeById);

    // Sanity-check: the root scan part should not be considered new.
    if (depth === 0 && isNew) {
      throw new Error('Root scan part cannot be new.');
    }

    if (isNew) {
      // If the scan part is new, add a suggestion for it.
      suggestions.push({
        scanId,
        scanVideoId,
        operation: 'add',
        scanPartId: scanPart.id,
        parentScanPartId: scanPartParentId,
        scanPart,
        agentCreated: true,
      });
      // No need to recurse further into this branch since the entire subtree is new.
      return;
    }
    const children: ScanPart[] = (scanPart as any)[SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH[depth]] ?? [];
    for (const child of children) {
      recursiveAddSuggestions(child, scanPart.id, depth + 1);
    }
  };
  recursiveAddSuggestions(scanRootAfter, '', 0);

  return suggestions;
};

/**
 * End-to-end convenience: fetch, apply one or more pure functional edit, save. This is the
 * non-React equivalent of the useSaveScanData hook.
 */
export const editAndSaveScan = async (
  client: KaiProApiClient,
  scanId: string,
  editScanRootOptimized: (scanRootOptimized: ScanRootOptimized) => ScanRootOptimized | Promise<ScanRootOptimized>,
): Promise<ScanRootOptimized> => {
  const scanRootOptimizedBefore = await getScanRootOptimizedForScan(client, scanId);
  const scanRootOptimizedAfter = await editScanRootOptimized(scanRootOptimizedBefore);
  await saveScanDataDeltaFromScanRoots(client, scanId, scanRootOptimizedBefore, scanRootOptimizedAfter);
  return scanRootOptimizedAfter;
};
